/**
 * MetaTagManager - Utility for dynamically updating HTML meta tags for social sharing
 *
 * This utility handles updating Open Graph, Twitter Card, and other meta tags
 * when content changes dynamically (like in modals) to ensure proper social sharing.
 */

class MetaTagManager {
  constructor() {
    // Store original meta tags to restore later
    this.originalMetaTags = {};
    this.hasStoredOriginals = false;
  }

  /**
   * Store the original meta tags before making changes
   */
  storeOriginalMetaTags() {
    if (this.hasStoredOriginals) {
      return;
    }

    const metaTagsToStore = [
      'og:title',
      'og:description',
      'og:image',
      'og:url',
      'og:type',
      'og:site_name',
      'twitter:card',
      'twitter:title',
      'twitter:description',
      'twitter:image',
      'twitter:site',
      'description',
      'title',
    ];

    metaTagsToStore.forEach(property => {
      const element = this.getMetaElement(property);
      if (element) {
        this.originalMetaTags[property] = element.getAttribute('content') || element.textContent;
      }
    });

    // Store original document title
    this.originalMetaTags.title = document.title;
    this.hasStoredOriginals = true;
  }

  /**
   * Get meta element by property/name
   */
  getMetaElement(property) {
    // Try property first (for og: tags)
    let element = document.querySelector(`meta[property="${property}"]`);

    // Try name attribute (for twitter: and description tags)
    if (!element) {
      element = document.querySelector(`meta[name="${property}"]`);
    }

    // Special case for title
    if (!element && property === 'title') {
      element = document.querySelector('title');
    }

    return element;
  }

  /**
   * Update or create a meta tag
   */
  updateMetaTag(property, content) {
    if (!content) {
      return;
    }

    let element = this.getMetaElement(property);

    if (property === 'title') {
      // Update document title
      document.title = content;
      return;
    }

    if (element) {
      // Update existing meta tag
      element.setAttribute('content', content);
    } else {
      // Create new meta tag
      element = document.createElement('meta');

      // Determine if it should use property or name attribute
      if (property.startsWith('og:') || property.startsWith('fb:')) {
        element.setAttribute('property', property);
      } else {
        element.setAttribute('name', property);
      }

      element.setAttribute('content', content);
      document.head.appendChild(element);
    }
  }

  /**
   * Update meta tags for a channel/movie/series
   */
  updateChannelMetaTags(channel, baseUrl = window.location.origin) {
    if (!channel) {
      return;
    }

    // Store originals before making changes
    this.storeOriginalMetaTags();

    const title = channel.title || 'BKFC';
    const description = this.truncateDescription(
      channel.description || 'Watch amazing content on BKFC',
    );
    const image = this.getBestImage(channel);
    const url = this.getCurrentUrl();
    const type = this.getContentType(channel);

    // Update document title
    this.updateMetaTag('title', `${title} | BKFC`);

    // Update Open Graph tags
    this.updateMetaTag('og:title', title);
    this.updateMetaTag('og:description', description);
    this.updateMetaTag('og:image', image);
    this.updateMetaTag('og:url', url);
    this.updateMetaTag('og:type', type);
    this.updateMetaTag('og:site_name', 'BKFC');

    // Update Twitter Card tags
    this.updateMetaTag('twitter:card', 'summary_large_image');
    this.updateMetaTag('twitter:title', title);
    this.updateMetaTag('twitter:description', description);
    this.updateMetaTag('twitter:image', image);
    this.updateMetaTag('twitter:site', '@BKFCTV'); // Update with actual Twitter handle

    // Update standard meta description
    this.updateMetaTag('description', description);

    // Additional meta tags for better SEO
    if (channel.year) {
      this.updateMetaTag('video:release_date', channel.year);
    }

    if (channel.rating) {
      this.updateMetaTag('video:rating', channel.rating);
    }

    if (channel.genres && Array.isArray(channel.genres)) {
      this.updateMetaTag('video:genre', channel.genres.join(', '));
    }

    console.log('🏷️ MetaTagManager: Updated meta tags for:', title);
  }

  /**
   * Get the best available image for social sharing
   */
  getBestImage(channel) {
    // Priority order: poster > wallpaper > channel_logo. Social cards are
    // landscape; `spotlight_poster` (CMS "Key Art") is the 2:3 portrait asset
    // and is deliberately excluded.
    const imageOptions = [channel.poster, channel.wallpaper, channel.channel_logo];

    for (const image of imageOptions) {
      if (image && typeof image === 'string' && image.trim()) {
        return image;
      }
    }

    // Fallback to a default BKFC image
    return `${window.location.origin}/assets/images/bkfc-social-share.jpg`;
  }

  /**
   * Determine content type for Open Graph
   */
  getContentType(channel) {
    if (channel.programming_type === 'Movie' || channel.type === 'movie') {
      return 'video.movie';
    } else if (channel.programming_type === 'Series' || channel.type === 'series') {
      return 'video.tv_show';
    }
    return 'video.other';
  }

  /**
   * Truncate description to optimal length for social sharing
   */
  truncateDescription(description, maxLength = 160) {
    if (!description || description.length <= maxLength) {
      return description;
    }

    // Find the last space before maxLength to avoid cutting words
    const truncated = description.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');

    if (lastSpace > maxLength * 0.8) {
      // Only use space if it's not too far back
      return truncated.substring(0, lastSpace) + '...';
    }

    return truncated + '...';
  }

  /**
   * Get current URL for og:url
   */
  getCurrentUrl() {
    return window.location.href;
  }

  /**
   * Restore original meta tags
   */
  restoreOriginalMetaTags() {
    if (!this.hasStoredOriginals) {
      return;
    }

    Object.entries(this.originalMetaTags).forEach(([property, content]) => {
      if (property === 'title') {
        document.title = content;
      } else {
        this.updateMetaTag(property, content);
      }
    });

    console.log('🏷️ MetaTagManager: Restored original meta tags');
  }

  /**
   * Clear stored original meta tags (useful for cleanup)
   */
  clearStoredOriginals() {
    this.originalMetaTags = {};
    this.hasStoredOriginals = false;
  }

  /**
   * Force refresh of social media crawlers (for testing)
   * This won't work in production but useful for development
   */
  debugSocialTags() {
    const currentUrl = window.location.href;

    console.group('🏷️ MetaTagManager: Current Social Tags');
    console.log('URL:', currentUrl);
    console.log('Title:', document.title);
    console.log('og:title:', this.getMetaElement('og:title')?.getAttribute('content'));
    console.log('og:description:', this.getMetaElement('og:description')?.getAttribute('content'));
    console.log('og:image:', this.getMetaElement('og:image')?.getAttribute('content'));
    console.log('og:url:', this.getMetaElement('og:url')?.getAttribute('content'));
    console.log('twitter:card:', this.getMetaElement('twitter:card')?.getAttribute('content'));
    console.groupEnd();

    // Provide testing URLs
    console.group('🧪 Social Media Debuggers');
    console.log(
      'Facebook Debugger:',
      `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(currentUrl)}`,
    );
    console.log('Twitter Validator:', `https://cards-dev.twitter.com/validator`);
    console.log(
      'LinkedIn Inspector:',
      `https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(currentUrl)}`,
    );
    console.groupEnd();
  }
}

// Create singleton instance
const metaTagManager = new MetaTagManager();

export default metaTagManager;
